/**
 * Class implementing a progressive backoff timeout strategy.
 * Useful for retrying operations (e.g., network requests) with increasing delays.
 */
class ProgressiveBackoff {
  /**
   * Creates a new ProgressiveBackoff instance.
   * @param {Object} [options={}] - Configuration options.
   * @param {number} [options.initialDelay=1000] - Initial delay in milliseconds.
   * @param {number} [options.maxDelay=30000] - Maximum delay in milliseconds.
   * @param {number} [options.factor=2] - Multiplier for exponential backoff.
   * @param {boolean} [options.jitter=true] - Whether to apply jitter to delays.
   */
  constructor({
    initialDelay = 1000,    // initial delay in milliseconds
    maxDelay = 30000,       // maximum delay
    factor = 2,         // exponential backoff factor
    jitter = true           // add random jitter to delays
  } = {}) {
    /**
     * @private
     * @type {number}
     */
    this.initialDelay = initialDelay;

    /**
     * @private
     * @type {number}
     */
    this.maxDelay = maxDelay;

    /**
     * @private
     * @type {number}
     */
    this.factor = factor;

    /**
     * @private
     * @type {number}
     */
    this.jitter = jitter;

    /**
     * @private
     * @type {number}
     */
    this.currentDelay = initialDelay;
  }

  /**
   * Waits for the current delay (optionally with jitter), then increases the delay.
   * @returns {Promise<number>} - Resolves after waiting; returns the delay used in milliseconds.
   */
  async wait() {
    const delay = this._getDelayWithOptionalJitter();
    await this._sleep(delay);
    this._increaseDelay();
    return delay;
  }

  /**
   * Resets the delay back to the initial delay.
   */
  reset() {
    this.currentDelay = this.initialDelay;
    if (this._timeout) {
      clearTimeout(this._timeout);
    }
  }

  /**
   * Calculates the current delay, applying jitter if enabled.
   * @private
   * @returns {number} - Delay in milliseconds.
   */
  _getDelayWithOptionalJitter() {
    if (!this.jitter) return this.currentDelay;

    // Jitter adds randomness: between 0.5x and 1.5x of the current delay
    const jitterFactor = 0.5 + Math.random(); 
    return Math.min(this.currentDelay * jitterFactor, this.maxDelay);
  }

  /**
   * Sleeps for the specified number of milliseconds.
   * @private
   * @param {number} ms - Milliseconds to sleep.
   * @returns {Promise<void>}
   */
  _sleep(ms) {
    return new Promise(resolve => {
      this._timeout = setTimeout(resolve, ms);
    });
  }

  /**
   * Increases the delay based on the factor, up to the max delay.
   * @private
   */
  _increaseDelay() {
    this.currentDelay = Math.min(this.currentDelay * this.factor, this.maxDelay);
  }
}


export default ProgressiveBackoff;